import Clarity from '@microsoft/clarity';

export const CLARITY_PROJECT_ID = 'q60kgsmajq';

/**
 * Initialize Microsoft Clarity with the project ID
 * This should be called once at application startup
 */
export const initClarity = () => {
  try {
    Clarity.init(CLARITY_PROJECT_ID);
    console.log('Clarity initialized successfully');
  } catch (error) {
    console.error('Failed to initialize Clarity:', error);
  }
};

/**
 * Track page views with enhanced metadata
 * @param {Object} metadata - Additional data to track with the page view
 * @param {string} metadata.customPageId - Custom identifier for the page (defaults to current path)
 * @param {string} metadata.pageType - Type of page (e.g., 'product', 'category', 'home')
 * @param {string} metadata.pageCategory - Category the page belongs to
 */
export const trackPageView = (metadata = {}) => {
  try {
    // Generate a unique page ID based on the current path
    const path = window.location.pathname;
    const customPageId = metadata.customPageId || path;
    
    // Set page-specific tags
    if (metadata.pageType) {
      Clarity.setTag('page_type', metadata.pageType);
    }
    
    if (metadata.pageCategory) {
      Clarity.setTag('page_category', metadata.pageCategory);
    }
    
    // Call identify for each page view to ensure optimal tracking
    Clarity.identify(null, null, customPageId);
    
    // Track the page view as a custom event for more detail
    Clarity.event('page_view', {
      path,
      title: document.title,
      ...metadata
    });
  } catch (error) {
    console.error('Failed to track Clarity page view:', error);
  }
};

/**
 * Track modal interactions
 * @param {string} modalName - Name of the modal (e.g., 'signup_modal', 'coverage_modal')
 * @param {string} action - Action performed (e.g., 'open', 'close', 'step_change')
 * @param {Object} metadata - Additional data to track with the interaction
 */
export const trackModalInteraction = (modalName, action, metadata = {}) => {
  try {
    Clarity.event('modal_interaction', {
      modal_name: modalName,
      action, // 'open', 'close', 'step_change', etc.
      ...metadata
    });
  } catch (error) {
    console.error('Failed to track modal interaction:', error);
  }
};

/**
 * Track form interactions
 * @param {string} formName - Name of the form (e.g., 'signup_form', 'contact_form')
 * @param {string} action - Action performed (e.g., 'start', 'step_complete', 'submit')
 * @param {Object} metadata - Additional data to track with the interaction
 */
export const trackFormInteraction = (formName, action, metadata = {}) => {
  try {
    Clarity.event('form_interaction', {
      form_name: formName,
      action, // 'start', 'step_complete', 'submit', 'abandon', etc.
      ...metadata
    });
  } catch (error) {
    console.error('Failed to track form interaction:', error);
  }
};

/**
 * Track user journey steps
 * @param {string} journeyName - Name of the journey (e.g., 'signup', 'package_selection')
 * @param {string} stepName - Name of the step in the journey
 * @param {Object} metadata - Additional data to track with the journey step
 */
export const trackJourneyStep = (journeyName, stepName, metadata = {}) => {
  try {
    // Set the current journey and step as tags
    Clarity.setTag('journey', journeyName);
    Clarity.setTag('journey_step', stepName);
    
    // Also track as an event for more detailed analysis
    Clarity.event('journey_step', {
      journey: journeyName,
      step: stepName,
      ...metadata
    });
  } catch (error) {
    console.error('Failed to track journey step:', error);
  }
};

/**
 * Track successful conversions
 * @param {string} conversionType - Type of conversion (e.g., 'signup_completed', 'package_purchased')
 * @param {Object} metadata - Additional data to track with the conversion
 */
export const trackConversion = (conversionType, metadata = {}) => {
  try {
    Clarity.event('conversion', {
      conversion_type: conversionType,
      ...metadata
    });
  } catch (error) {
    console.error('Failed to track conversion:', error);
  }
};

/**
 * Track form abandonment
 * @param {string} formName - Name of the form that was abandoned
 * @param {string} step - Last step completed before abandonment
 * @param {string} reason - Reason for abandonment (e.g., 'navigation_away', 'closed_modal')
 * @param {Object} metadata - Additional data to track with the abandonment
 */
export const trackFormAbandonment = (formName, step, reason, metadata = {}) => {
  try {
    Clarity.event('form_abandonment', {
      form_name: formName,
      last_step: step,
      reason, // 'navigation_away', 'closed_modal', 'session_timeout', etc.
      ...metadata
    });
  } catch (error) {
    console.error('Failed to track form abandonment:', error);
  }
};

/**
 * Track custom events with metadata
 * @param {string} eventName - Name of the event to track
 * @param {Object} metadata - Additional data to track with the event
 */
export const trackEvent = (eventName, metadata = {}) => {
  try {
    Clarity.event(eventName, metadata);
  } catch (error) {
    console.error(`Failed to track Clarity event ${eventName}:`, error);
  }
};

/**
 * Set a custom tag in Clarity
 * @param {string} key - Tag key
 * @param {string|number|boolean} value - Tag value
 */
export const setCustomTag = (key, value) => {
  try {
    Clarity.setTag(key, value);
  } catch (error) {
    console.error('Failed to set Clarity tag:', error);
  }
};